import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface TopSellingProduct {
  id: number;
  product_id?: number;
  productName: string;
  image_path: string;
  barCode?: string;
  unit_price?: number;
  total_sold?: number;
  link?: string;
}

interface TopSellingProductsContextType {
  products: TopSellingProduct[];
  loading: boolean;
  error: string | null;
  refetchTopSellingProducts: () => Promise<void>;
  getProductImage: (productId?: number | string, image?: string) => string;
}

const TopSellingProductsContext = createContext<
  TopSellingProductsContextType | undefined
>(undefined);

export function TopSellingProductsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE_URL = import.meta.env.VITE_LYRAZONE_IMAGE_URL;

  const [products, setProducts] = useState<TopSellingProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getProductImage = useCallback(
    (productId?: number | string, image?: string) => {
      if (!productId || !image) return "";
      return `${IMAGE_URL}/uploads/products/${productId}/${image}`;
    },
    [IMAGE_URL],
  );

  const refetchTopSellingProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Expected backend route:
      // GET `${BASE_URL}/products/top-selling?limit=5`
      const res = await fetch(`${BASE_URL}/top-selling?limit=5`);
      const result = await res.json();
      if (result?.success) {
        const normalized = (result.data || []).map((item: any) => ({
          id: item.id ?? item.product_id,
          product_id: item.product_id ?? item.id,
          productName: item.productName || item.product_name || "",
          image_path: item.image_path || "",
          barCode: item.barCode || item.bar_code || "",
          unit_price: Number(item.unit_price || item.active_price || 0),
          total_sold: Number(item.total_sold || 0),
          link:
            item.link ||
            (item.barCode || item.bar_code
              ? `/single-product/${item.barCode || item.bar_code}`
              : "#"),
        }));

        setProducts(normalized);
      } else {
        setProducts([]);
        setError(result?.message || "Unable to fetch top selling products.");
      }
    } catch (err) {
      console.error("Top selling products fetch error:", err);
      setProducts([]);
      setError("Something went wrong while fetching top selling products.");
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    refetchTopSellingProducts();
  }, [refetchTopSellingProducts]);

  const value = useMemo(
    () => ({
      products,
      loading,
      error,
      refetchTopSellingProducts,
      getProductImage,
    }),
    [products, loading, error, refetchTopSellingProducts, getProductImage],
  );

  return (
    <TopSellingProductsContext.Provider value={value}>
      {children}
    </TopSellingProductsContext.Provider>
  );
}

export function useTopSellingProducts() {
  const context = useContext(TopSellingProductsContext);

  if (!context) {
    throw new Error(
      "useTopSellingProducts must be used within a TopSellingProductsProvider",
    );
  }

  return context;
}